import React, { useState, useEffect } from 'react';
import { Plus, Settings as SettingsIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import api from '../services/api';
import { WIDGETS, DEFAULT_WIDGETS_USER, DEFAULT_WIDGETS_ADMIN } from '../components/dashboard/widgets';
import CustomizeDashboardModal from '../components/dashboard/CustomizeDashboardModal';
import { useAuth } from '../contexts/AuthContext';

// Event types that change something a dashboard widget might be displaying.
// When SSE delivers any of these, we invalidate every widget query so the
// stats / charts / activity feed re-fetch without the operator hitting refresh.
const WIDGET_RELEVANT_EVENTS = new Set([
  'lead_created',
  'lead_updated',
  'lead_deleted',
  'lead_status_updated',
  'lead_priority_updated',
  'lead_assigned',
  'lead_stage_updated',
  'note_added',
  'note_deleted',
  'task_created',
  'task_updated',
  'task_completed',
  'bulk_email_created',
  'bulk_email_started',
  'bulk_email_completed',
]);

const Dashboard = () => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.permissions?.view_all_leads === true || currentUser?.role === 'superadmin';
  const allowedWidgetIds = Object.values(WIDGETS)
    .filter((w) => w.role === 'any' || (w.role === 'admin' && isAdmin))
    .map((w) => w.id);
  const defaultLayout = isAdmin ? DEFAULT_WIDGETS_ADMIN : DEFAULT_WIDGETS_USER;

  const [customizeOpen, setCustomizeOpen] = useState(false);

  // Layout = the user's saved order. Defaults to per-role default until the
  // user customizes. Persisted via PATCH /auth/me/dashboard so it follows
  // them across devices.
  const [layout, setLayout] = useState(() => {
    const saved = currentUser?.dashboardWidgets;
    return Array.isArray(saved) && saved.length > 0 ? saved : defaultLayout;
  });

  const saveLayoutMutation = useMutation(
    (widgets) => api.patch('/auth/me/dashboard', { widgets }).then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('Dashboard saved');
        queryClient.invalidateQueries('me');
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Save failed'),
    }
  );
  const saveLayout = (widgets) => {
    setLayout(widgets);
    saveLayoutMutation.mutate(widgets);
  };

  // Render widgets resolved from the layout array. Each widget chooses its
  // own column span via its `size`.
  const CustomizableWidgets = ({ isAdmin }) => {
    const visible = layout
      .map((id) => WIDGETS[id])
      .filter(Boolean)
      .filter((w) => w.role === 'any' || (w.role === 'admin' && isAdmin));
    if (visible.length === 0) return null;
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {visible.map((w) => {
          const span = w.size === 'lg' ? 'col-span-1 md:col-span-2 lg:col-span-4'
            : w.size === 'md' ? 'col-span-1 md:col-span-2 lg:col-span-2'
            : 'col-span-1';
          return (
            <div key={w.id} className={`${span} bg-white border border-gray-200 rounded-lg p-4 shadow-sm`}>
              <w.Render />
            </div>
          );
        })}
      </div>
    );
  };

  // Loading / error states are now per-widget — each widget owns its own
  // useQuery and renders its own skeleton + empty state.
  //
  // SSE bridge: a single page-level listener invalidates every widget
  // query whenever the server broadcasts a lead/note/task/bulk-email
  // event. Avoids each widget opening its own EventSource (would be 5-10
  // simultaneous connections per tab) while still giving them real-time
  // refresh when a teammate moves a lead or adds a note. Server already
  // filters broadcasts by org so cross-tenant leaks aren't possible.
  useEffect(() => {
    const _token = localStorage.getItem('token');
    if (!_token) return;
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
    const es = new EventSource(`${apiBase}/events?token=${encodeURIComponent(_token)}`);

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (WIDGET_RELEVANT_EVENTS.has(data.type)) {
          // Match every key starting with ['widget', ...] — covers
          // every widget's useQuery in this codebase. Cheap to fire
          // because react-query coalesces refetches in flight.
          queryClient.invalidateQueries({ predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'widget' });
        }
      } catch (_) { /* ignore parse errors / connection pings */ }
    };

    return () => es.close();
  }, [queryClient]);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-2 text-gray-600">
            Overview and statistics of your CRM system
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center gap-2">
          <button
            onClick={() => setCustomizeOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-md text-sm font-medium"
            title="Show / hide / reorder widgets"
          >
            <SettingsIcon size={14} /> Customize
          </button>
          <Link
            to="/leads/new"
            className="btn-primary"
          >
            <Plus size={16} className="mr-2" />
            New Lead
          </Link>
        </div>
      </div>

      <CustomizableWidgets isAdmin={isAdmin} />
      <CustomizeDashboardModal
        isOpen={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        onSave={saveLayout}
        currentLayout={layout}
        allowedWidgetIds={allowedWidgetIds}
        defaultLayout={defaultLayout}
      />

      {/* The dashboard is now 100% widget-driven. Stats cards, My Tasks
          panel, Quick Actions, Status / Source distributions and Recent
          Activities are all entries in the WIDGETS catalog (see
          components/dashboard/widgets.js). Operators reorder / show / hide
          each one via the Customize modal — nothing on this page is
          hard-coded any more. */}
    </div>
  );
};

export default Dashboard; 